import {Label, RelativeTime} from '@primer/react'
import {ProfileReference} from '@github-ui/profile-reference'
import {clsx} from 'clsx'
import type {ElementType, ReactNode} from 'react'
import {StateType} from './types/list-types'
import {getAuthorFilterValue} from './helpers/derive-attributed-username'
import styles from './PullsListItem.module.css'
import metadataLinkStyles from '../shared/MetadataLink.module.css'

export type AuthorFilterLinkProps = {
  as?: ElementType
  to?: string
  href?: string
  preload?: 'intent'
  reloadDocument?: boolean
  state?: unknown
}

interface PullsListItemTimestampContainerProps {
  createdAt: Date
  updatedAt: Date
  closedAt?: Date
  mergedAt?: Date
  prState: StateType
  author: string
  authorDisplayName?: string
  isAgent?: boolean
  attributedUsername?: string
  onAuthorClick?: (author: string) => void
  getAuthorLinkProps?: (filterValue: string) => AuthorFilterLinkProps | undefined
  showUpdatedAt?: boolean
}

export function AuthorFilterControl({
  login,
  displayName,
  testId,
  children,
  onAuthorClick,
  authorLinkProps,
}: {
  login: string
  displayName?: string
  testId: string
  children: ReactNode
  onAuthorClick?: (author: string) => void
  authorLinkProps?: AuthorFilterLinkProps
}) {
  // The server sends a single ready-to-render label that already includes the handle when the org setting requires it.
  const ariaLabel = `Filter by author ${displayName || login}`

  if (authorLinkProps) {
    const {as: Component = 'a', ...rest} = authorLinkProps
    return (
      <Component
        className={clsx(styles.filterLink, metadataLinkStyles.Link)}
        data-testid={testId}
        aria-label={ariaLabel}
        {...rest}
      >
        {children}
      </Component>
    )
  }

  if (onAuthorClick) {
    return (
      <button
        type="button"
        onClick={() => onAuthorClick(getAuthorFilterValue(login))}
        className={clsx(styles.filterLink, metadataLinkStyles.Link)}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        {children}
      </button>
    )
  }

  return children
}

export const PullsListItemTimestampContainer = ({
  prState,
  author,
  authorDisplayName,
  createdAt,
  updatedAt,
  closedAt,
  mergedAt,
  isAgent = false,
  attributedUsername,
  onAuthorClick,
  getAuthorLinkProps,
  showUpdatedAt = true,
}: PullsListItemTimestampContainerProps) => {
  const hasBeenUpdated = createdAt.getTime() !== updatedAt.getTime()
  const isOpen = prState === StateType.Opened
  const isMerged = prState === StateType.Merged
  // Fall back to updatedAt while backends that don't yet send closedAt/mergedAt are still deployed.
  const closedTime = isMerged ? (mergedAt ?? closedAt ?? updatedAt) : (closedAt ?? updatedAt)

  // The server label already includes the handle when required by the organization setting.
  const authorContent = authorDisplayName || <ProfileReference login={author} profileName={author} isAgent={isAgent} />

  return (
    <span data-testid="timestamp-container">
      <span className={styles.timestampSeparator} aria-hidden="true">
        &middot;
      </span>
      {!isOpen && <span>by </span>}
      {attributedUsername && (
        <>
          <AuthorFilterControl
            login={attributedUsername}
            testId="attributed-author-filter-link"
            onAuthorClick={onAuthorClick}
            authorLinkProps={getAuthorLinkProps?.(getAuthorFilterValue(attributedUsername))}
          >
            <ProfileReference login={attributedUsername} profileName={attributedUsername} isAgent={false} />
          </AuthorFilterControl>
          <span> with </span>
        </>
      )}
      <AuthorFilterControl
        login={author}
        displayName={authorDisplayName}
        testId="author-filter-link"
        onAuthorClick={onAuthorClick}
        authorLinkProps={getAuthorLinkProps?.(getAuthorFilterValue(author))}
      >
        {authorContent}
      </AuthorFilterControl>
      {isAgent ? (
        <Label variant="secondary" className="ml-1">
          AI
        </Label>
      ) : null}
      {isOpen ? (
        <>
          <span> opened </span>
          <RelativeTime date={createdAt} />
          {showUpdatedAt && hasBeenUpdated && (
            <span>
              {' '}
              <span>&middot;</span> <span>Updated</span> <RelativeTime date={updatedAt} />
            </span>
          )}
        </>
      ) : (
        <>
          <span>{isMerged ? ' was merged ' : ' was closed '}</span>
          <RelativeTime date={closedTime} />
        </>
      )}
    </span>
  )
}
